import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {Thumbnail} from '@Components/thumbnail';
import type {FillObject} from '@PosterWhiteboard/classes/fill.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {getAssetUrl} from '@Utils/s3.util';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import styles from './base-fill-type-options.module.scss';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';

interface BaseFillTypeOptionsProps {
  panelId: string;
  fillObject: FillObject;
  onChangeFillType(newType: FillTypes): void;
  allowPanelHeightToBeSetInternally?: boolean;
}

const TEXT_FILL_OPTIONS = [FillTypes.SOLID, FillTypes.LINEAR_GRADIENT, FillTypes.RADIAL_GRADIENT];

export function BaseFillTypeOptions({allowPanelHeightToBeSetInternally = true, ...props}: BaseFillTypeOptionsProps): ReactElement | null {
  useEffect((): void => {
    updateHeightInRedux(props.fillObject.fillType);
  }, [props.fillObject.fillType]);

  useEffect((): void => {
    setTimeout((): void => {
      updateHeightInRedux(props.fillObject.fillType);
    }, 200);
  }, []);

  const getTextFromType = (type: FillTypes): string => {
    switch (type) {
      case FillTypes.RADIAL_GRADIENT:
        return window.i18next.t('pmwjs_gradient_radial');
      case FillTypes.LINEAR_GRADIENT:
        return window.i18next.t('pmwjs_gradient_linear');
      case FillTypes.SOLID:
      default:
        return window.i18next.t('pmwjs_background_solid');
    }
  };

  const getThumbnailFromType = (type: FillTypes): string => {
    switch (type) {
      case FillTypes.RADIAL_GRADIENT:
        return 'radial-fill';
      case FillTypes.LINEAR_GRADIENT:
        return 'linear-fill';
      case FillTypes.SOLID:
      default:
        return 'solid-fill';
    }
  };

  const updateHeightInRedux = (newType: FillTypes): void => {
    if (!allowPanelHeightToBeSetInternally) {
      return;
    }

    updateOptionsPanelHeight(props.panelId, newType === FillTypes.SOLID ? '206px' : '254px');
  };

  const getTextFillTypeOptions = (): ReactElement[] => {
    return TEXT_FILL_OPTIONS.map((option) => {
      return (
        <ContextualMenuThumbnailItem
          key={option}
          thumbnail={
            <Thumbnail
              className={`spacing-p-l-3 spacing-p-r-3 ${styles.thumb}`}
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: getAssetUrl(`poster-editor-thumbnails/${getThumbnailFromType(option)}.${getCompatibleImageFileExtension('webp')}`),
              }}
            />
          }
          onClick={(): void => {
            props.onChangeFillType(option);
            updateHeightInRedux(option);
          }}
          selectionState={ThumbnailItemSelectionState.BORDER}
          isSelected={props.fillObject.fillType === option}
          text={getTextFromType(option)}
        />
      );
    });
  };
  return <ul className="flex-h-row spacing-m-0"> {getTextFillTypeOptions()}</ul>;
}
